import {
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  InputSignal,
  signal,
  Signal,
  untracked,
  viewChild,
  WritableSignal,
} from '@angular/core';
import { WebSocketService } from '../../services/websocket-service';
import { DisplayAnimations } from '../../directives/display-animations';
import { Icon, IconType } from '../icon/icon';
import { SportType } from '../../models/sport.model';
import { TranslateModule } from '@ngx-translate/core';
import {
  createVideoSourceController,
  VideoSourceController,
} from '../../helpers/video-source.helpers';
import { BufferService } from '../../services/buffer-service';
import { BufferSyncAction } from '../../models/buffer-sync-action.model';

@Component({
  selector: 'app-screen-video',
  imports: [DisplayAnimations, Icon, TranslateModule],
  template: `
    @if (!hasBegin() && !clientIsReady()) {
      <div class="overlay">
        <div appDisplayAnimations class="ready">
          <h2>{{ 'pages.display.overlay.ready' | translate }}</h2>
          <p>{{ 'pages.display.overlay.start_now' | translate }}</p>
          <button (click)="initialPlay()">
            <app-icon [size]="2" color="#000000" icon="play" />
            {{ 'common.actions.start' | translate }}
          </button>
        </div>
      </div>
    } @else if (!hasBegin() && clientIsReady()) {
      <div class="overlay">
        <div appDisplayAnimations class="start">
          <h2>
            <app-icon [size]="2.5" [icon]="sportVerb()" />
            {{ 'common.actions.' + sportVerb() | translate }}
          </h2>
          <p>{{ 'pages.display.overlay.start_training' | translate }}</p>
        </div>
      </div>
    } @else if (!isPlaying()) {
      <div class="overlay"></div>
    }
    <video #video [muted]="muted()"></video>
  `,
  styleUrl: './screen-video.scss',
  hostDirectives: [DisplayAnimations],
})
export class ScreenVideo {
  private static readonly minPlaybackRate: number = 0.25;
  private static readonly maxPlaybackRate: number = 4;

  private readonly bufferService: BufferService = inject(BufferService);
  private readonly webSocketService: WebSocketService = inject(WebSocketService);

  readonly videoUrl: InputSignal<string> = input.required<string>();
  private readonly videoRef: Signal<ElementRef<HTMLVideoElement>> =
    viewChild.required<ElementRef<HTMLVideoElement>>('video');
  readonly muted: Signal<boolean> = computed(() => this.webSocketService.config()?.mute ?? true);
  readonly resumeTime: Signal<number> = computed(
    () => this.webSocketService.contentInformation()?.videoResumeTime ?? 0,
  );

  readonly isPlaying: Signal<boolean> = computed(
    () => this.webSocketService.videoStatus() === 'play',
  );

  readonly hasBegin: Signal<boolean> = this.webSocketService.hasBegin;
  readonly rate: Signal<number> = computed(() => this.webSocketService.rate());

  readonly sportVerb: Signal<IconType> = computed(() => {
    const sport: SportType = this.webSocketService.contentInformation()?.sport || 'cycling';
    switch (sport) {
      case 'running':
        return 'run';
      case 'rowing':
        return 'row';
      default:
        return 'ride';
    }
  });

  clientIsReady: WritableSignal<boolean> = signal(false);
  private timeInterval: ReturnType<typeof setInterval> | null = null;
  private sourceController: VideoSourceController | null = null;
  private sourceVideoElement: HTMLVideoElement | null = null;
  private autoplayAttempt: boolean = false;

  constructor() {
    effect(() => {
      const videoUrl: string = this.videoUrl();
      const video: HTMLVideoElement = this.videoRef().nativeElement;
      const currentTime: number = video.currentTime;
      this.getSourceController().attach(videoUrl);

      if (untracked(this.hasBegin)) {
        video.addEventListener(
          'loadedmetadata',
          () => {
            video.currentTime = currentTime;
            if (this.isPlaying()) this.play();
          },
          { once: true },
        );
        return;
      }

      this.clientIsReady.set(false);
      this.tryPrepareVideo(videoUrl);
    });

    effect(() => {
      switch (this.webSocketService.videoStatus()) {
        case 'play':
          this.play();
          break;
        case 'pause':
          this.pause();
          break;
        case 'stop':
          this.stop();
          break;
      }
      this.applyPlaybackRate(this.rate());
    });
  }

  initialPlay(): void {
    this.webSocketService.emitVideoLoaded();
    this.seekToResumeTime();
    this.clientIsReady.set(true);
  }

  play(): void {
    this.videoRef().nativeElement.play();
    this.clearTimeInterval();
    this.timeInterval = setInterval(() => {
      this.syncVideoTime();
      this.webSocketService.emitTimeChange(Math.round(this.videoRef().nativeElement.currentTime));
    }, 1000);
  }

  pause(): void {
    this.videoRef().nativeElement.pause();
    this.clearTimeInterval();
  }

  stop(): void {
    this.clearTimeInterval();
    const video: HTMLVideoElement = this.videoRef().nativeElement;
    video.pause();
    this.sourceController?.reset();
  }

  private clearTimeInterval(): void {
    if (this.timeInterval !== null) {
      clearInterval(this.timeInterval);
      this.timeInterval = null;
    }
  }

  private tryPrepareVideo(videoUrl: string): void {
    if (!videoUrl) return;

    const video: HTMLVideoElement = this.videoRef().nativeElement;

    const attemptPlayback = (): void => {
      if (this.autoplayAttempt) return;

      const playResult: Promise<void> | undefined = video.play();
      if (!playResult) {
        this.handleAutoplaySuccess();
        return;
      }

      playResult
        .then(() => this.handleAutoplaySuccess())
        .catch(() => {
          if (this.autoplayAttempt) return;
          video.pause();
          this.clientIsReady.set(false);
        });
    };

    if (video.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
      attemptPlayback();
      return;
    }

    const handleCanPlay = (): void => {
      video.removeEventListener('canplay', handleCanPlay);
      attemptPlayback();
    };

    video.addEventListener('canplay', handleCanPlay, { once: true });
  }

  private seekToResumeTime(): void {
    const video: HTMLVideoElement = this.videoRef().nativeElement;
    const targetTime: number = this.resumeTime();

    if (!targetTime) return;

    if (video.readyState >= HTMLMediaElement.HAVE_METADATA) {
      video.currentTime = targetTime;
      return;
    }

    const handleLoadedMetadata = (): void => {
      video.currentTime = targetTime;
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
    };

    video.addEventListener('loadedmetadata', handleLoadedMetadata);
  }

  private syncVideoTime(): void {
    const targetTime: number | undefined = this.webSocketService.broadcastData()?.videoTime;

    if (targetTime === undefined) return;

    const video: HTMLVideoElement = this.videoRef().nativeElement;
    const action: BufferSyncAction = this.bufferService.getSyncAction(
      video.currentTime,
      targetTime,
      this.rate(),
    );

    this.applyPlaybackRate(action.playbackRate);

    if (action.type === 'seek') {
      video.currentTime = action.targetTime;
    }
  }

  private applyPlaybackRate(playbackRate: number): void {
    const video: HTMLVideoElement = this.videoRef().nativeElement;
    const safePlaybackRate: number = Math.min(
      ScreenVideo.maxPlaybackRate,
      Math.max(ScreenVideo.minPlaybackRate, playbackRate),
    );

    if (video.playbackRate === safePlaybackRate) return;

    video.playbackRate = safePlaybackRate;
  }

  private handleAutoplaySuccess(): void {
    if (this.autoplayAttempt) return;

    const video: HTMLVideoElement = this.videoRef().nativeElement;
    video.pause();
    this.initialPlay();
  }

  private getSourceController(): VideoSourceController {
    const video: HTMLVideoElement = this.videoRef().nativeElement;

    if (this.sourceController && this.sourceVideoElement === video) {
      return this.sourceController;
    }

    this.sourceController?.destroy();
    this.sourceVideoElement = video;
    this.sourceController = createVideoSourceController(video);
    return this.sourceController;
  }

  ngOnDestroy(): void {
    this.clearTimeInterval();
    const video: HTMLVideoElement = this.videoRef().nativeElement;
    video.pause();
    this.sourceController?.destroy();
    this.sourceController = null;
    this.sourceVideoElement = null;
  }
}
